import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Trophy, Check, X, HelpCircle, AlertCircle } from "lucide-react";

/**
 * Mastermind Game
 *
 * Rules:
 * - Guess 4 colors.
 * - Feedback:
 *   - Black: Correct color, Correct position.
 *   - White: Correct color, Wrong position.
 */
const MastermindGame = () => {
    const navigate = useNavigate();

    // --- Constants ---
    const COLORS = ["red", "blue", "green", "yellow", "purple", "orange"];
    const COLOR_CLASSES = {
        red: "bg-red-500",
        blue: "bg-blue-500",
        green: "bg-green-500",
        yellow: "bg-yellow-400",
        purple: "bg-purple-500",
        orange: "bg-orange-500",
    };
    const CODE_LENGTH = 4;
    const MAX_TURNS = 10;

    // --- State ---
    const [secretCode, setSecretCode] = useState([]);
    const [guesses, setGuesses] = useState(Array(MAX_TURNS).fill(Array(CODE_LENGTH).fill(null))); // History of board
    const [feedback, setFeedback] = useState(Array(MAX_TURNS).fill(null)); // History of feedback
    const [currentTurn, setCurrentTurn] = useState(0);
    const [currentGuess, setCurrentGuess] = useState(Array(CODE_LENGTH).fill(null)); // Current input
    const [gameStatus, setGameStatus] = useState("playing"); // playing, won, lost
    const [showRules, setShowRules] = useState(false);

    // --- Logic ---

    const startNewGame = () => {
        // Generate Secret Code
        const code = [];
        for(let i=0; i<CODE_LENGTH; i++) {
            code.push(COLORS[Math.floor(Math.random() * COLORS.length)]);
        }
        setSecretCode(code);
        setGuesses(Array(MAX_TURNS).fill(Array(CODE_LENGTH).fill(null)));
        setFeedback(Array(MAX_TURNS).fill(null));
        setCurrentTurn(0);
        setCurrentGuess(Array(CODE_LENGTH).fill(null));
        setGameStatus("playing");
    };

    useEffect(() => {
        startNewGame();
    }, []);

    const handleColorSelect = (color) => {
        if (gameStatus !== "playing") return;

        // Fill first empty slot
        const nextIdx = currentGuess.indexOf(null);
        if (nextIdx !== -1) {
            const newGuess = [...currentGuess];
            newGuess[nextIdx] = color;
            setCurrentGuess(newGuess);
        }
    };

    const handleClearSlot = (index) => {
        if (gameStatus !== "playing") return;
        const newGuess = [...currentGuess];
        newGuess[index] = null;
        setCurrentGuess(newGuess);
    };

    const calculateFeedback = (guess, code) => {
        let black = 0;
        let white = 0;
        const codeFreq = {};
        const guessFreq = {};

        // 1. Calculate Black Pegs (Exact matches)
        const unmatchedGuessIndices = [];
        const unmatchedCodeIndices = [];

        for (let i = 0; i < CODE_LENGTH; i++) {
            if (guess[i] === code[i]) {
                black++;
            } else {
                unmatchedGuessIndices.push(i);
                unmatchedCodeIndices.push(i);
            }
        }

        // 2. Calculate White Pegs (Color matches)
        // Count frequencies of remaining items
        unmatchedCodeIndices.forEach(idx => {
            const color = code[idx];
            codeFreq[color] = (codeFreq[color] || 0) + 1;
        });

        unmatchedGuessIndices.forEach(idx => {
             const color = guess[idx];
             if (codeFreq[color] > 0) {
                 white++;
                 codeFreq[color]--;
             }
        });

        return { black, white };
    };

    const handleSubmitGuess = () => {
        if (currentGuess.includes(null)) return; // Incomplete

        const result = calculateFeedback(currentGuess, secretCode);
        
        // Update History
        const newGuesses = [...guesses];
        const newFeedback = [...feedback];
        newGuesses[currentTurn] = currentGuess;
        newFeedback[currentTurn] = result;
        
        setGuesses(newGuesses);
        setFeedback(newFeedback);

        // Check Win/Loss
        if (result.black === CODE_LENGTH) {
            setGameStatus("won");
        } else if (currentTurn >= MAX_TURNS - 1) {
            setGameStatus("lost");
        } else {
            setCurrentTurn(currentTurn + 1);
            setCurrentGuess(Array(CODE_LENGTH).fill(null));
        }
    };

    // --- Rendering ---
    
    // Render the secret code (hidden unless game over)
    const renderSecretCode = () => {
        if (gameStatus === "playing") {
             return (
                 <div className="flex bg-gray-800 p-4 rounded-lg shadow-inner gap-4 justify-center">
                     {Array(CODE_LENGTH).fill(null).map((_, i) => (
                         <div key={i} className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center text-gray-400 text-xl font-bold">
                             ?
                         </div>
                     ))}
                 </div>
             );
        }
        return (
             <div className="flex bg-gray-800 p-4 rounded-lg shadow-lg gap-4 justify-center animate-fade-in">
                 {secretCode.map((color, i) => (
                     <div key={i} className={`w-10 h-10 rounded-full ${COLOR_CLASSES[color]} shadow-md`} />
                 ))}
             </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col">
            {/* Header */}
            <div className="bg-white shadow-sm sticky top-0 z-30">
                <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
                    <button
                        onClick={() => navigate("/games")}
                        className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        <ArrowLeft className="h-5 w-5 mr-1" />
                        <span className="font-medium hidden sm:inline">Back to Games</span>
                    </button>
                    <h1 className="text-xl font-bold text-gray-800">Mastermind</h1>
                    <button
                        onClick={() => setShowRules(!showRules)}
                        className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                    >
                        <HelpCircle className="h-6 w-6" />
                    </button>
                </div>
            </div>

            <main className="flex-grow max-w-lg mx-auto w-full p-4 flex flex-col gap-6">
                
                {/* Rules */}
                {showRules && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                        <h3 className="font-bold text-lg mb-2 flex items-center"><AlertCircle className="w-5 h-5 mr-2" /> How to Play</h3>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Guess the 4-color code.</li>
                            <li><strong>Black Peg:</strong> Right Color, Right Position.</li>
                            <li><strong>White Peg:</strong> Right Color, Wrong Position.</li>
                            <li>You have 10 turns.</li>
                        </ul>
                    </div>
                )}
                
                {/* Secret Code Area */}
                <div className="text-center">
                     <h3 className="text-gray-500 font-bold mb-2 uppercase text-xs tracking-wider">Secret Code</h3>
                     {renderSecretCode()}
                </div>

                {/* Game Board */}
                <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-gray-200">
                    <div className="flex flex-col-reverse divide-y divide-gray-100 bg-gray-50">
                        {guesses.map((row, i) => {
                            // Render past rows or current row
                            const isCurrent = i === currentTurn && gameStatus === "playing";
                            const rowData = isCurrent ? currentGuess : row;
                            const fb = feedback[i];

                            return (
                                <div key={i} className={`flex items-center p-2 sm:p-3 ${isCurrent ? "bg-white ring-2 ring-inset ring-blue-200" : ""}`}>
                                    {/* Turn Number */}
                                    <div className="w-8 text-xs font-mono text-gray-400 font-bold">{i + 1}</div>
                                    
                                    {/* Pegs */}
                                    <div className="flex-1 flex justify-center gap-2 sm:gap-4">
                                        {Array(CODE_LENGTH).fill(null).map((_, slotIdx) => {
                                            const color = rowData[slotIdx];
                                            return (
                                                <button 
                                                    key={slotIdx}
                                                    onClick={() => isCurrent && handleClearSlot(slotIdx)}
                                                    className={`
                                                        w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 
                                                        ${color ? COLOR_CLASSES[color] : "bg-transparent border-gray-200 border-dashed"}
                                                        ${isCurrent ? "cursor-pointer hover:bg-gray-50" : "cursor-default"}
                                                        transition-all shadow-sm
                                                    `}
                                                    disabled={!isCurrent}
                                                />
                                            );
                                        })}
                                    </div>
                                    
                                    {/* Feedback */}
                                    <div className="w-16 grid grid-cols-2 gap-1 p-1">
                                        {fb ? (
                                            <>
                                                {Array(fb.black).fill(0).map((_, k) => <div key={`b-${k}`} className="w-3 h-3 rounded-full bg-black shadow-sm mx-auto" title="Right Color, Right Place" />)}
                                                {Array(fb.white).fill(0).map((_, k) => <div key={`w-${k}`} className="w-3 h-3 rounded-full bg-white border border-gray-300 shadow-sm mx-auto" title="Right Color, Wrong Place" />)}
                                            </>
                                        ) : (
                                            isCurrent ? (
                                                <button 
                                                    onClick={handleSubmitGuess}
                                                    disabled={currentGuess.includes(null)}
                                                    className={`
                                                        col-span-2 w-full h-full rounded flex items-center justify-center 
                                                        ${currentGuess.includes(null) ? "text-gray-300" : "bg-green-100 text-green-600 hover:bg-green-200"}
                                                        transition-colors
                                                    `}
                                                >
                                                   <Check className="w-5 h-5" />
                                                </button>
                                            ) : null
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Controls */}
                {gameStatus === "playing" ? (
                    <div className="bg-white p-4 rounded-xl shadow-lg border border-gray-200">
                         <div className="flex justify-between items-center gap-2">
                             {COLORS.map((color) => (
                                 <button
                                     key={color}
                                     onClick={() => handleColorSelect(color)}
                                     className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full ${COLOR_CLASSES[color]} shadow-md hover:scale-110 active:scale-95 transition-transform border-2 border-transparent hover:border-gray-200`}
                                 />
                             ))}
                         </div>
                    </div>
                ) : (
                    <div className="text-center animate-bounce-in">
                        {gameStatus === "won" ? (
                             <div className="mb-4">
                                 <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-2" />
                                 <h2 className="text-3xl font-bold text-gray-800">Code Broken!</h2>
                                 <p className="text-gray-600">You solved it in {currentTurn + 1} turns.</p>
                             </div>
                        ) : (
                             <div className="mb-4">
                                 <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-2">
                                     <X className="w-10 h-10" />
                                 </div>
                                 <h2 className="text-3xl font-bold text-gray-800">Game Over</h2>
                                 <p className="text-gray-600">Better luck next time!</p>
                             </div>
                        )}
                        
                        <button
                            onClick={startNewGame}
                            className="px-8 py-3 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-700 shadow-lg active:scale-95 transition-transform"
                        >
                            Play Again
                        </button>
                    </div>
                )}
            </main>
        </div>
    );
};

export default MastermindGame;
